// T57: "Moja dostępność" — pracownik klika dni, w które może pracować (głównie weekendy).
// Układający składy (admin, zarządza / przypisuje do eventów — T146) widzą na tym samym ekranie dostępność zespołu:
// liczba osób na dzień, klik → nazwiska.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast, RoleBadge, fullName, Modal, leaveLabel, TimeSelect } from '../components/UI';
import api from '../utils/api';

const MONTHS = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
const DOW = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
const key = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const todayKey = () => new Date().toISOString().slice(0, 10);
const LICENSE_CATEGORIES = ['B', 'B+E', 'C', 'C+E', 'D', 'T']; // jak filtr składu w Events.js
// T160: kolejność grup w liście dnia (dostępność eventowych)
const DAY_GROUPS = [
  ['🎪 Eventowi', (u) => u.role === 'EVENT' && !u.animationWorkEnabled],
  ['🏢 Biuro, magazyn i inni jeżdżący na eventy', (u) => u.role !== 'EVENT' && !u.animationWorkEnabled],
  ['🎈 Animatorki / animatorzy', (u) => !!u.animationWorkEnabled],
];
const hasLicense = (u, cat) => !cat || String(u.drivingLicense || '').split(',').map(x => x.trim()).includes(cat);

// Siatka miesiąca: tablica tygodni, każdy = 7 pól (null poza miesiącem)
function monthGrid(y, m) {
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7; // poniedziałek = 0
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export default function AvailabilityPage() {
  const { hasAvailability, canAssignEvents, seesStaffAbsences } = useAuth();
  // T146 (19.10): dostępność eventowych widzi tylko ten, kto układa składy (admin, zarządza / przypisuje do eventów) — samo zatwierdzanie godzin nie
  const seesTeam = canAssignEvents;
  // T127: Moja (kto sam deklaruje) · Pracownicy stali (nieobecności) · Eventowi (deklarowana dostępność)
  const modes = [hasAvailability && ['mine', 'Moja'], seesStaffAbsences && ['staff', '🏢 Pracownicy stali'], seesTeam && ['team', '🎪 Eventowi']].filter(Boolean);
  const [mode, setMode] = useState(modes[0]?.[0] || 'mine');
  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">{mode === 'mine' ? 'Moja dostępność' : 'Dostępność zespołu'}</h2>
        {modes.length > 1 && (
          <div className="filter-pills">
            {modes.map(([v, l]) => <button key={v} className={`filter-pill ${mode === v ? 'active' : ''}`} onClick={() => setMode(v)}>{l}</button>)}
          </div>
        )}
      </div>
      {mode === 'mine' ? <MyCalendar /> : mode === 'staff' ? <StaffAbsences /> : <TeamCalendar />}
    </div>
  );
}

// T127 (16.10): „Pracownicy stali” — kto kiedy jest nieobecny (urlop, L4, praca zdalna). Admin, kadry, rozliczający urlopy.
const ABS_KIND = (t) => t === 'SICK' ? 'sick' : t === 'REMOTE_WORK' ? 'remote' : 'leave';
const ABS_STYLE = {
  leave: { icon: '🏖', label: 'urlop', color: '#0369A1', bg: '#E0F2FE' },
  sick: { icon: '🤒', label: 'L4', color: '#B91C1C', bg: '#FEE2E2' },
  remote: { icon: '💻', label: 'zdalnie', color: '#7C3AED', bg: '#EDE9FE' },
};
const dPL = (k) => new Date(k + 'T12:00:00').toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });

function StaffAbsences() {
  const { isAdmin, canAccounting, canSettleLeaves } = useAuth();
  const onlyTeam = !isAdmin && !canAccounting && !canSettleLeaves; // T129: koordynator widzi swój zespół
  const mo = useMonth();
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(todayKey());
  const [kind, setKind] = useState('');            // '' | leave | sick | remote
  const [withPending, setWithPending] = useState(true);
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    try { setRows(await api.getAbsences(mo.from, mo.to)); } catch (e) { show(e.message, 'error'); }
  }, [mo.from, mo.to]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const shown = rows.filter(r => (withPending || r.status !== 'PENDING') && (!kind || ABS_KIND(r.leaveType) === kind));
  const on = (k) => shown.filter(r => r.startDate <= k && r.endDate >= k);
  const pickedRows = picked ? on(picked) : [];

  return (
    <>
      <div className="card">
        {onlyTeam && <div style={{ fontSize: 11, color: '#64748B', marginBottom: 8 }}>Twój zespół — osoby, którym jesteś przełożonym (także pośrednio), i te, za których przełożonego zastępujesz.</div>}
        <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center', marginBottom: 10 }}>
          <MonthNav {...mo} />
          <div className="filter-pills">
            {[['', 'Wszystko'], ['leave', '🏖 Urlopy'], ['sick', '🤒 L4'], ['remote', '💻 Zdalnie']].map(([v, l]) => (
              <button key={v} className={`filter-pill ${kind === v ? 'active' : ''}`} onClick={() => setKind(v)}>{l}</button>
            ))}
          </div>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
            <input type="checkbox" checked={withPending} onChange={e => setWithPending(e.target.checked)} /> także oczekujące (przerywana ramka)
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {DOW.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748B', padding: 4 }}>{d}</div>)}
          {mo.weeks.flat().map((d, i) => {
            if (!d) return <div key={i} />;
            const k = key(mo.y, mo.m, d);
            const dow = new Date(mo.y, mo.m, d).getDay();
            const weekend = dow === 0 || dow === 6;
            const list = weekend ? [] : on(k);
            const by = { leave: [], sick: [], remote: [] };
            list.forEach(r => by[ABS_KIND(r.leaveType)].push(r));
            return (
              <div key={k} onClick={() => setPicked(k)}
                style={{ ...cellBase, cursor: 'pointer', textAlign: 'left', padding: '4px 5px', minHeight: 58,
                  background: picked === k ? '#DBEAFE' : weekend ? '#F1F5F9' : '#fff',
                  border: picked === k ? '1px solid #2563EB' : k === todayKey() ? '1px solid #F59E0B' : cellBase.border }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: weekend ? '#94A3B8' : '#334155' }}>{d}</div>
                {Object.entries(by).filter(([, l]) => l.length).map(([kk, l]) => (
                  <div key={kk} title={l.map(r => fullName(r.user)).join(', ')}
                    style={{ fontSize: 10, color: ABS_STYLE[kk].color, background: ABS_STYLE[kk].bg, borderRadius: 4, padding: '0 3px', marginTop: 2,
                      border: l.every(r => r.status === 'PENDING') ? `1px dashed ${ABS_STYLE[kk].color}` : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ABS_STYLE[kk].icon} {l.length === 1 ? `${l[0].user.firstName} ${(l[0].user.lastName || "").slice(0, 1)}.` : `${l.length} os.`}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {picked && (
        <div className="card">
          <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>
            {picked === todayKey() ? 'Dziś' : new Date(picked + 'T12:00:00').toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })} — nieobecni ({pickedRows.length})
          </h3>
          {pickedRows.length ? pickedRows.map(r => {
            const st = ABS_STYLE[ABS_KIND(r.leaveType)];
            return (
              <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0', flexWrap: 'wrap' }}>
                <b>{fullName(r.user)}</b> <RoleBadge role={r.user.role} />
                <span style={{ color: st.color }}>{st.icon} {leaveLabel(r.leaveType)}{r.hours ? ` (${r.hours} h)` : ""}</span>
                <span style={{ color: '#64748B' }}>{dPL(r.startDate)}–{dPL(r.endDate)}</span>
                {r.status === 'PENDING' && <span style={{ fontSize: 10, color: '#D97706' }}>⏳ czeka na zatwierdzenie</span>}
              </div>
            );
          }) : <div style={{ color: '#94A3B8', fontSize: 12 }}>Wszyscy w pracy.</div>}
        </div>
      )}
      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>Nieobecności w miesiącu ({shown.length})</h3>
        {shown.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Rodzaj</th><th>Od – do</th><th>Dni rob.</th><th>Status</th></tr></thead>
              <tbody>
                {shown.map(r => (
                  <tr key={r.id} style={r.status === 'PENDING' ? { opacity: 0.7 } : undefined}>
                    <td>{fullName(r.user)} <RoleBadge role={r.user.role} /></td>
                    <td style={{ color: ABS_STYLE[ABS_KIND(r.leaveType)].color }}>{ABS_STYLE[ABS_KIND(r.leaveType)].icon} {leaveLabel(r.leaveType)}</td>
                    <td>{dPL(r.startDate)} – {dPL(r.endDate)}</td>
                    <td>{r.hours ? `${r.hours} h` : r.totalDays}</td>
                    <td style={{ fontSize: 11 }}>{r.status === 'PENDING' ? '⏳ czeka' : '✓ zatwierdzony'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ color: '#94A3B8', fontSize: 12 }}>Brak nieobecności w tym miesiącu.</div>}
      </div>
      {ToastComponent}
    </>
  );
}


function useMonth() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const prev = () => setYm(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 });
  const next = () => setYm(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });
  const from = key(ym.y, ym.m, 1);
  const to = key(ym.y, ym.m, new Date(ym.y, ym.m + 1, 0).getDate());
  return { ...ym, prev, next, from, to, weeks: monthGrid(ym.y, ym.m) };
}

const MonthNav = ({ y, m, prev, next }) => (
  <div className="flex gap-8" style={{ alignItems: 'center' }}>
    <button className="btn btn-outline btn-sm" onClick={prev}>‹</button>
    <b style={{ minWidth: 150, textAlign: 'center' }}>{MONTHS[m]} {y}</b>
    <button className="btn btn-outline btn-sm" onClick={next}>›</button>
  </div>
);

const cellBase = { borderRadius: 8, padding: '8px 4px', textAlign: 'center', fontSize: 13, minHeight: 44, border: '1px solid #E2E8F0', userSelect: 'none' };

function MyCalendar() {
  const mo = useMonth();
  const [days, setDays] = useState({}); // 'YYYY-MM-DD' → { fromTime, toTime, note } (pusty obiekt = cały dzień)
  const [detail, setDetail] = useState(null); // T67: okno „godziny i uwaga" dla jednego dnia; T100: { dates: [...] } dla wielu
  // T100: zaznaczanie wielu dni — Ctrl/⌘+klik (pojedyncze), Shift+klik (zakres), na telefonie tryb „Zaznacz kilka dni"
  const [sel, setSel] = useState(new Set());
  const [anchor, setAnchor] = useState(null);
  const [multi, setMulti] = useState(false);
  const [saving, setSaving] = useState(false);
  const { show, ToastComponent } = useToast();
  const today = todayKey();

  const load = useCallback(async () => {
    try { setDays(Object.fromEntries((await api.getMyAvailability(mo.from, mo.to)).map(d => [d.date, d]))); }
    catch (e) { show(e.message, 'error'); }
  }, [mo.from, mo.to]);
  useEffect(() => { load(); }, [load]);

  const change = async (add, remove) => {
    setSaving(true);
    try {
      await api.setMyAvailability(add, remove);
      setDays(d => { const n = { ...d }; add.forEach(x => { n[x] = { date: x }; }); remove.forEach(x => { delete n[x]; }); return n; });
    } catch (e) { show(e.message, 'error'); }
    setSaving(false);
  };
  const toggle = (k) => { if (k < today || saving) return; days[k] ? change([], [k]) : change([k], []); };
  const allKeys = mo.weeks.flat().filter(Boolean).map(d => key(mo.y, mo.m, d));
  const clearSel = () => { setSel(new Set()); setAnchor(null); };
  useEffect(() => { clearSel(); }, [mo.from]); // inny miesiąc — nowe zaznaczenie
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && sel.size && !detail) clearSel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, detail]);
  const onCell = (e, k) => {
    if (k < today || saving) return;
    if (e.shiftKey && anchor) {
      // zakres od ostatnio klikniętego dnia do tego (bez dni z przeszłości)
      const [a, b] = anchor < k ? [anchor, k] : [k, anchor];
      setSel(prev => { const n = new Set(prev); allKeys.filter(x => x >= a && x <= b && x >= today).forEach(x => n.add(x)); return n; });
      return;
    }
    if (e.ctrlKey || e.metaKey || multi) {
      setSel(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
      setAnchor(k);
      return;
    }
    clearSel();
    setAnchor(k);
    toggle(k);
  };
  const selected = [...sel].sort();
  const selOn = () => { const ks = selected.filter(k => !days[k]); if (ks.length) change(ks, []); clearSel(); };
  const selOff = () => { const ks = selected.filter(k => days[k]); if (ks.length) change([], ks); clearSel(); };
  const selHours = () => setDetail({ dates: selected, fromTime: '08:00', toTime: '18:00', note: '', allDay: false });
  // T67: okno z godzinami „od–do" i uwagą do zaznaczonego dnia
  const openDetail = (e, k) => {
    e.stopPropagation();
    const d = days[k] || {};
    setDetail({ date: k, fromTime: d.fromTime || '', toTime: d.toTime || '', note: d.note || '', allDay: !d.fromTime });
  };
  const saveDetail = async () => {
    const d = detail;
    setDetail(null);
    try {
      if (d.dates) {
        // T100: godziny dla wielu dni naraz (dni zostają zaznaczone jako dostępne)
        const r = await api.setAvailabilityDetail({ dates: d.dates, fromTime: d.allDay ? null : d.fromTime, toTime: d.allDay ? null : d.toTime, note: d.note });
        show(r.message || 'Zapisane');
        clearSel();
        load();
        return;
      }
      await api.setAvailabilityDetail({
        date: d.date,
        fromTime: d.allDay ? null : d.fromTime,
        toTime: d.allDay ? null : d.toTime,
        note: d.note,
      });
      show('Zapisane');
      load();
    } catch (e) { show(e.message, 'error'); }
  };
  const weekendKeys = () => mo.weeks.flat().filter(Boolean).map(d => ({ k: key(mo.y, mo.m, d), dow: new Date(mo.y, mo.m, d).getDay() })).filter(x => (x.dow === 0 || x.dow === 6) && x.k >= today).map(x => x.k);
  const markWeekends = () => { const ks = weekendKeys().filter(k => !days[k]); if (ks.length) change(ks, []); };
  const clearMonth = () => { const ks = Object.keys(days).filter(k => k >= today && k >= mo.from && k <= mo.to); if (ks.length && window.confirm(`Wyczyścić ${ks.length} zaznaczonych dni w tym miesiącu?`)) change([], ks); };
  const count = Object.keys(days).filter(k => k >= mo.from && k <= mo.to).length;

  return (
    <>
      <div className="card" style={{ fontSize: 12, color: '#475569' }}>
        Kliknij dni, w które <b>możesz pracować</b> — koordynator widzi to przy układaniu składu eventu i w pierwszej kolejności
        sięga po osoby dostępne. Zaznaczaj z wyprzedzeniem, najlepiej cały miesiąc. Dni z przeszłości są zablokowane.
        <div style={{ marginTop: 4 }}>
          <b>Kilka dni naraz:</b> Ctrl+klik — pojedyncze dni, Shift+klik — od…do (jak w Wordzie). Na telefonie: przycisk
          „☑ Zaznacz kilka dni”. Potem jednym przyciskiem: <i>mogę</i>, <i>ustaw godziny</i> albo <i>nie mogę</i>.
        </div>
      </div>
      <div className="card">
        <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <MonthNav {...mo} />
          <div className="flex gap-6">
            <button className={`btn btn-sm ${multi ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setMulti(m => !m); if (multi) clearSel(); }} title="Klikanie zaznacza dni zamiast od razu je zapisywać — wygodne na telefonie">☑ {multi ? 'Zaznaczanie: wł.' : 'Zaznacz kilka dni'}</button>
            <button className="btn btn-outline btn-sm" onClick={markWeekends} disabled={saving}>Zaznacz wszystkie weekendy</button>
            <button className="btn btn-ghost btn-sm" onClick={clearMonth} disabled={saving || !count}>Wyczyść miesiąc</button>
          </div>
        </div>
        {selected.length > 0 && (
          <div className="flex gap-6 flex-wrap" style={{ alignItems: 'center', marginBottom: 10, padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12 }}>
            <b>Zaznaczono {selected.length} {selected.length === 1 ? 'dzień' : 'dni'}:</b>
            <button className="btn btn-success btn-sm" onClick={selOn} disabled={saving}>✓ Mogę pracować</button>
            <button className="btn btn-primary btn-sm" onClick={selHours} disabled={saving}>🕒 Ustaw godziny…</button>
            <button className="btn btn-danger btn-sm" onClick={selOff} disabled={saving}>✗ Nie mogę (odznacz)</button>
            <button className="btn btn-ghost btn-sm" onClick={clearSel} style={{ marginLeft: 'auto' }}>Anuluj zaznaczenie (Esc)</button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {DOW.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748B', padding: 4 }}>{d}</div>)}
          {mo.weeks.flat().map((d, i) => {
            if (!d) return <div key={i} />;
            const k = key(mo.y, mo.m, d);
            const picked = sel.has(k);
            const dow = new Date(mo.y, mo.m, d).getDay();
            const weekend = dow === 0 || dow === 6;
            const past = k < today;
            const on = !!days[k];
            return (
              <div key={k} onClick={(e) => onCell(e, k)} title={past ? 'Dzień z przeszłości' : (multi ? 'Kliknij, aby dodać do zaznaczenia' : (on ? 'Kliknij, aby odznaczyć (Ctrl/Shift — zaznacz kilka)' : 'Kliknij, aby zaznaczyć: mogę pracować (Ctrl/Shift — zaznacz kilka)'))}
                style={{ ...cellBase, cursor: past ? 'not-allowed' : 'pointer', opacity: past ? 0.4 : 1,
                  background: on ? '#059669' : (weekend ? '#F1F5F9' : '#fff'), color: on ? '#fff' : (weekend ? '#0F172A' : '#334155'),
                  fontWeight: on || weekend ? 700 : 400, outline: picked ? '3px solid #F59E0B' : (k === today ? '2px solid #2563EB' : 'none'), outlineOffset: picked ? -3 : 0,
                  boxShadow: picked ? 'inset 0 0 0 100px rgba(245,158,11,0.18)' : 'none' }}>
                {picked && <span style={{ fontSize: 10, marginRight: 2 }}>☑</span>}{d}
                {on && (
                  <div style={{ fontSize: 9, lineHeight: 1.2 }}>
                    {days[k].fromTime ? `${days[k].fromTime}–${days[k].toTime}` : 'mogę'}
                    {days[k].note && <span title={days[k].note}> 📝</span>}
                    <div onClick={(e) => openDetail(e, k)} title="Godziny i uwaga" style={{ textDecoration: 'underline', cursor: 'pointer' }}>✎ godziny</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: '#64748B' }}>
          Zaznaczone dni w tym miesiącu: <b>{count}</b>. Zmiany zapisują się od razu.
          Domyślnie dzień jest całodniowy — kliknij „✎ godziny", jeśli możesz tylko w części dnia (np. od 15:00 po swojej pracy).
        </div>
      </div>
      {detail && (
        <Modal title={detail.dates ? `Godziny dostępności — ${detail.dates.length} ${detail.dates.length === 1 ? 'dzień' : 'dni'}` : `Dostępność ${new Date(detail.date).toLocaleDateString('pl-PL')}`} onClose={() => setDetail(null)}
          dirty={!!(detail.note || (!detail.allDay && (detail.fromTime || detail.toTime)))}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={detail.allDay} onChange={e => setDetail({ ...detail, allDay: e.target.checked })} />
              Cały dzień
            </label>
            {!detail.allDay && (
              <div className="flex gap-8">
                <div className="input-group flex-1"><label>Od</label><TimeSelect value={detail.fromTime} onChange={e => setDetail({ ...detail, fromTime: e.target.value })} /></div>
                <div className="input-group flex-1"><label>Do</label><TimeSelect value={detail.toTime} onChange={e => setDetail({ ...detail, toTime: e.target.value })} /></div>
              </div>
            )}
            {detail.dates && <div style={{ fontSize: 11, color: '#64748B' }}>Dni: {detail.dates.map(x => x.slice(8, 10) + '.' + x.slice(5, 7)).join(', ')} — zostaną zaznaczone jako dostępne z tymi godzinami.</div>}
            <div className="input-group"><label>Uwaga (opcjonalnie){detail.dates && <span style={{ fontWeight: 400, color: '#94A3B8' }}> — puste = bez zmiany uwag</span>}</label><input value={detail.note} onChange={e => setDetail({ ...detail, note: e.target.value })} placeholder="np. tylko okolice Poznania, mogę kierować" maxLength={200} /></div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setDetail(null)}>Anuluj</button>
              <button className="btn btn-primary" onClick={saveDetail} disabled={!detail.allDay && (!detail.fromTime || !detail.toTime)}>Zapisz</button>
            </div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </>
  );
}

function TeamCalendar() {
  const mo = useMonth();
  const [rows, setRows] = useState([]); // [{date, user}]
  const [picked, setPicked] = useState(null);
  const [flt, setFlt] = useState({ gender: '', license: '', animator: false }); // T152
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    try { setRows(await api.getAvailability(mo.from, mo.to)); } catch (e) { show(e.message, 'error'); }
  }, [mo.from, mo.to]);
  useEffect(() => { load(); setPicked(null); }, [load]);

  const byDay = {};
  // T152: filtry działają też na liczby w kalendarzu (np. ile kobiet z prawem jazdy B jest dostępnych)
  const fits = (u) => (!flt.gender || u.gender === flt.gender) && hasLicense(u, flt.license) && (!flt.animator || u.animationWorkEnabled);
  const fltOn = !!(flt.gender || flt.license || flt.animator);
  rows.filter(r => fits(r.user)).forEach(r => { (byDay[r.date] = byDay[r.date] || []).push(r); });
  const pickedUsers = picked ? (byDay[picked] || []) : [];

  return (
    <>
      <div className="card" style={{ fontSize: 12, color: '#475569' }}>
        Pracownicy eventowi i osoby z pracą na eventach: liczba osób, które zadeklarowały dostępność danego dnia. Kliknij dzień, aby zobaczyć nazwiska. Przy układaniu składu eventu
        ci sami ludzie mają zielony znacznik „✓ dostępny" i filtr „dostępni tego dnia".
      </div>
      <div className="card">
        <div className="flex gap-8 flex-wrap" style={{ marginBottom: 12, alignItems: 'center' }}>
          <MonthNav {...mo} />
          <select value={flt.gender} onChange={e => setFlt({ ...flt, gender: e.target.value })} style={{ fontSize: 12, padding: '3px 6px' }}>
            <option value="">płeć: dowolna</option><option value="K">kobiety</option><option value="M">mężczyźni</option>
          </select>
          <select value={flt.license} onChange={e => setFlt({ ...flt, license: e.target.value })} style={{ fontSize: 12, padding: '3px 6px' }}>
            <option value="">prawo jazdy: dowolne</option>
            {LICENSE_CATEGORIES.map(c => <option key={c} value={c}>prawo jazdy: {c}</option>)}
          </select>
          <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={flt.animator} onChange={e => setFlt({ ...flt, animator: e.target.checked })} /> 🎈 animator(ka)
          </label>
          {fltOn && <button className="btn btn-ghost btn-sm" onClick={() => setFlt({ gender: '', license: '', animator: false })}>wyczyść filtry</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {DOW.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748B', padding: 4 }}>{d}</div>)}
          {mo.weeks.flat().map((d, i) => {
            if (!d) return <div key={i} />;
            const k = key(mo.y, mo.m, d);
            const n = (byDay[k] || []).length;
            const dow = new Date(mo.y, mo.m, d).getDay();
            const weekend = dow === 0 || dow === 6;
            return (
              <div key={k} onClick={() => setPicked(k)}
                style={{ ...cellBase, cursor: 'pointer', background: picked === k ? '#DBEAFE' : (n ? '#ECFDF5' : (weekend ? '#F1F5F9' : '#fff')),
                  border: picked === k ? '1px solid #2563EB' : cellBase.border, fontWeight: weekend ? 700 : 400 }}>
                {d}<div style={{ fontSize: 10, color: n ? '#059669' : '#CBD5E1', fontWeight: 700 }}>{n ? `${n} os.` : '—'}</div>
              </div>
            );
          })}
        </div>
      </div>
      {picked && (
        <div className="card">
          <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>Dostępni {new Date(picked).toLocaleDateString('pl-PL')} ({pickedUsers.length}){fltOn ? ' — wg filtrów' : ''}</h3>
          {pickedUsers.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* T160 (25.10): grupy — eventowi, Biuro i inni jeżdżący na eventy, animatorki */}
              {DAY_GROUPS.map(([label, fits]) => { const list = pickedUsers.filter(r => fits(r.user)).sort((x, y) => fullName(x.user).localeCompare(fullName(y.user), 'pl')); return list.length ? (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4, borderBottom: '1px solid #E2E8F0', paddingBottom: 2 }}>{label} ({list.length})</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, fontSize: 12 }}>
                    {list.map(r => (
                      <div key={r.user.id}>
                        {fullName(r.user)} <RoleBadge role={r.user.role} />
                        <span style={{ fontSize: 10, color: '#64748B' }}>{r.user.gender === 'K' ? ' ♀' : r.user.gender === 'M' ? ' ♂' : ''}{r.user.drivingLicense ? ` 🚗 ${r.user.drivingLicense}` : ''}{r.user.animationWorkEnabled ? ' 🎈' : ''}</span>
                        {r.fromTime && <span style={{ color: '#D97706' }}> {r.fromTime}–{r.toTime}</span>}
                        {r.note && <div style={{ fontSize: 10, color: '#64748B' }}>📝 {r.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null; })}
            </div>
          ) : <div style={{ color: '#94A3B8', fontSize: 12 }}>{fltOn ? 'Nikt pasujący do filtrów.' : 'Nikt nie zadeklarował tego dnia.'}</div>}
        </div>
      )}
      {ToastComponent}
    </>
  );
}
